import { useCommissionAccess } from "@/hooks/useCommissionAccess";
import { canApproveCommission, commissionCopy } from "@/lib/app/commission-access";
import { Button } from "@/components/ui/button";
import { ConfirmDeletionDialog } from "@/components/ConfirmDeletionDialog";
import {
  getApproved,
  getPaid,
  getUnapproved,
  isApproved,
  isUnapproved,
} from "@/lib/app";
import { PiggyBank, Trash2Icon } from "lucide-react";
import { useAppStore } from "@/stores";
import {
  CommissionStatus,
  NestedCommission,
  NestedUser,
} from "@revelate/types";
import { Loader } from "@/components/Loader";
import { getAPI, getCalcUrl } from "@/lib/api";
import { format } from "date-fns";
import { useState } from "react";

interface ApproveButtonProps {
  commissions: NestedCommission[];
  updateCommissions: (
    previousStatus: CommissionStatus,
    newStatus: CommissionStatus,
    filterByCommission?: NestedCommission
  ) => Promise<void>;
  scopedUsers: NestedUser[];
  loading?: boolean;
}

export const ApproveButton = ({
  commissions,
  updateCommissions,
  scopedUsers,
  loading = false,
}: ApproveButtonProps) => {
  const { data: access } = useCommissionAccess();
  const eligible = commissions.filter(c => canApproveCommission(access, c.user_id));
  const admin = access?.role === "admin" || access?.role === "super_admin";
  const { currentUser, dateRange } = useAppStore((state) => state);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadLogs = async () => {
    if (!currentUser || !currentUser?.company_id || !dateRange) return;
    setIsDownloading(true);
    try {
      const isProjected = false;
      const isEphemeral = false; // DO save to DB

      const calcUrl = getCalcUrl(
        currentUser,
        dateRange,
        scopedUsers.map((user) => user.id),
        isProjected,
        isEphemeral,
        true
      );
      if (!calcUrl) return;

      const { logs } = await getAPI(calcUrl);

      if (!logs) return;
      // Create and download the text file
      const blob = new Blob([logs.join("\n")], {
        type: "text/plain;charset=utf-8",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `commission-logs-${dateRange?.from ? format(dateRange?.from, "yyyy-MM-dd") : ""}-to-${dateRange?.to ? format(dateRange?.to, "yyyy-MM-dd") : ""}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {eligible.length > 0 ? (
        <>
          {admin && <Button
            onClick={handleDownloadLogs}
            variant="outline"
            disabled={loading || isDownloading}
          >
            {isDownloading ? (
              <Loader size={20} className="mr-2.5" />
            ) : null}
            Download logs
          </Button>}

          {isUnapproved(eligible) ? (
            <Button
              onClick={() => updateCommissions("unapproved", "approved")}
              variant="default"
              disabled={loading}
            >
              {loading ? (
                <Loader size={20} stroke="white" className="mr-2.5" />
              ) : null}
              {commissionCopy.approveAll} ({getUnapproved(eligible).length})
            </Button>
          ) : admin && isApproved(commissions) ? (
            <Button
              onClick={() => updateCommissions("approved", "paid")}
              variant="default"
              disabled={loading}
            >
              <PiggyBank className="h-5 w-5 mr-2" />
              {commissionCopy.finalApprove} ({getApproved(commissions).length})
            </Button>
          ) : admin && getPaid(commissions).length > 0 ? (
            <ConfirmDeletionDialog
              key={`delete-commissions`}
              title="Reopen approved commissions?"
              description="These commissions will need both approvals again."
              deleteId={"all"}
              deleteOperation={async () => {
                await updateCommissions("paid", "unapproved");
              }}
            >
              <Button size="lg" variant="outline">
                <Trash2Icon className="h-5 w-5 mr-2" />
                {commissionCopy.reopen} ({getPaid(commissions).length})
              </Button>
            </ConfirmDeletionDialog>
          ) : (
            <></>
          )}
        </>
      ) : (
        <></>
      )}
    </div>
  );
};
